import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FilePdf, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import DictateButton from "../components/DictateButton";
import { CheckGroup } from "../components/forms/fields";
import { SignaturePad } from "../components/SignaturePad";
import { FORM_DEFS } from "../components/forms/paperworkForms";

export default function FillSignPage() {
  const { id, docKey } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [entry, setEntry] = useState(null);
  const [data, setData] = useState({});
  const [medDate, setMedDate] = useState(new Date().toISOString().slice(0, 10));
  const [medRows, setMedRows] = useState(Array.from({ length: 10 }, () => ({ name: "", dosage: "", frequency: "" })));
  const [medNotes, setMedNotes] = useState("");
  const [signerName, setSignerName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [sig, setSig] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [origUrl, setOrigUrl] = useState(null);
  const [origErr, setOrigErr] = useState(false);
  const [signing, setSigning] = useState(false);
  const [paperQ, setPaperQ] = useState("");
  const [paperHits, setPaperHits] = useState([]);
  const [paperPerson, setPaperPerson] = useState(null);
  const [paperFile, setPaperFile] = useState(null);
  const [paperBusy, setPaperBusy] = useState(false);

  useEffect(() => {
    if (paperQ.trim().length < 2 || paperPerson) { setPaperHits([]); return; }
    const t = setTimeout(() => {
      api.get("/document-library/people", { params: { q: paperQ.trim() } })
        .then((r) => setPaperHits(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [paperQ, paperPerson]);

  const uploadPaper = async () => {
    if (!paperPerson || !paperFile) { toast.error("Pick the person and choose the file"); return; }
    setPaperBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", paperFile);
      fd.append("person_type", paperPerson.type);
      fd.append("person_id", paperPerson.id);
      const r = await api.post(`/document-library/${docKey}/upload`, fd, { timeout: 120000 });
      toast.success(`Filed to ${r.data.filed_to}'s record`);
      navigate("/documents");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaperBusy(false);
    }
  };

  const def = FORM_DEFS[docKey] || null;
  const viewOnly = entry?.mode === "view";

  const downloadBase = async () => {
    try {
      const res = await api.get(`/document-library/${docKey}/download`, { responseType: "blob", timeout: 60000 });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${docKey}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    if (!id) {
      api.get("/document-library")
        .then((r) => {
          const t = (r.data || []).find((x) => x.key === docKey);
          if (!t) { toast.error("Unknown document"); navigate("/documents"); return; }
          if (t.mode === "link" && t.form_url) { navigate(t.form_url); return; }
          setEntry({ key: t.key, title: t.title, desc: t.note, mode: t.mode, signed: null, file: null });
        })
        .catch((e) => { toast.error(formatApiError(e)); navigate("/documents"); });
      return;
    }
    Promise.all([api.get(`/clients/${id}`), api.get(`/clients/${id}/paperwork`)])
      .then(([c, p]) => {
        setClient(c.data);
        setEntry((p.data || []).find((x) => x.key === docKey) || null);
        const intake = c.data.forms?.intake || {};
        const prof = c.data.forms?.profile || {};
        const cp = c.data.forms?.careplan || {};
        const schedRows = Object.entries(cp.schedule || {}).filter(([, s]) => s?.start || s?.end);
        setData({
          full_name: intake.full_name || "", dob: intake.dob || "", age: intake.age || "",
          gender: intake.gender || "", primary_phone: intake.primary_phone || intake.phone || "", email_addr: intake.email || "",
          street: intake.service_address || intake.address || "", ec_name: intake.ec_name || intake.emergency_contact_name || "",
          ec_phone: intake.ec_phone || intake.emergency_contact_phone || "", pc_name: intake.pc_name || intake.primary_contact || "",
          lockbox_code: (intake.home_access || "").match(/\b\d{3,6}\b/)?.[0] || "",
          client_name: intake.full_name || "", client_address: intake.service_address || intake.address || "",
          authorized_person: intake.rep_name || "", rep_type: intake.rep_type || "",
          emergency_phone: intake.ec_phone || intake.emergency_contact_phone || "", client_email: intake.email || "",
          effective_date: cp.effective_date || "",
          start_date: prof.start_date || cp.effective_date || "",
          shift1_days: schedRows.map(([day]) => day).join(", "),
          shift1_time: schedRows.map(([, s]) => `${s.start || ""}-${s.end || ""}`).join(", "),
          state: "PA",
        });
        const m = c.data.forms?.medications;
        if (m) {
          const rows = [...(m.entries || [])];
          while (rows.length < 10) rows.push({ name: "", dosage: "", frequency: "" });
          setMedRows(rows);
          setMedNotes(m.notes || "");
          if (m.date_reviewed) setMedDate(m.date_reviewed);
        }
      })
      .catch((e) => { toast.error(formatApiError(e)); navigate(`/clients/${id}/paperwork`); });
  }, [id, docKey, navigate]);

  useEffect(() => {
    if (!showOriginal || origUrl) return;
    let cancelled = false;
    api.get(`/document-library/${docKey}/view`, { responseType: "blob", timeout: 60000 })
      .then((r) => {
        if (!cancelled) setOrigUrl(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })));
      })
      .catch(() => { if (!cancelled) setOrigErr(true); });
    return () => { cancelled = true; };
  }, [showOriginal, docKey, origUrl]);

  if (!entry || (id && !client)) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setMedRow = (i, k, v) => setMedRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const renderField = (f) => (
    <div key={f.key} className={f.type === "textarea" || f.type === "checks" ? "md:col-span-2" : ""}>
      {f.type === "checks" ? (
        <CheckGroup label={f.label} options={f.options} values={data[f.key] || []} onChange={(v) => set(f.key, v)} tidPrefix={`field-${f.key}`} />
      ) : f.type === "radio" ? (
        <div className="space-y-1.5">
          <Label>{f.label}</Label>
          <div className="flex flex-wrap gap-1.5" data-testid={`field-${f.key}`}>
            {f.options.map((o) => (
              <button key={o} type="button" onClick={() => set(f.key, data[f.key] === o ? "" : o)}
                data-testid={`field-${f.key}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-md border px-3 py-1.5 text-sm transition-all ${
                  data[f.key] === o ? "border-primary bg-primary/10 font-medium text-primary" : "border-stone-200 bg-white text-stone-500 hover:border-stone-400 hover:text-stone-800"
                }`}>{o}</button>
            ))}
          </div>
        </div>
      ) : f.type === "textarea" ? (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <Label>{f.label}</Label>
            <DictateButton testid={`field-${f.key}-dictate`} onText={(t) => set(f.key, (data[f.key] ? data[f.key] + " " : "") + t)} />
          </div>
          <Textarea rows={2} value={data[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="border-stone-300 bg-white" data-testid={`field-${f.key}`} />
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label>{f.label}</Label>
          <Input type={f.type === "date" ? "date" : "text"} maxLength={f.type === "initials" ? 4 : undefined}
            value={data[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
            className={`border-stone-300 bg-white ${f.type === "initials" ? "w-24" : ""}`} data-testid={`field-${f.key}`} />
        </div>
      )}
    </div>
  );

  const buildEntries = () => {
    const entries = [];
    if (def?.meds) {
      entries.push({ label: "Date", value: medDate });
      medRows.filter((r) => r.name || r.dosage || r.frequency).forEach((r, i) =>
        entries.push({ label: `Medication ${i + 1}`, value: [r.name, r.dosage, r.frequency].filter(Boolean).join("  |  ") }));
      if (medNotes) entries.push({ label: "Additional Medications or Notes", value: medNotes });
    }
    (def?.sections || []).forEach((sec) =>
      sec.fields.forEach((f) => {
        const v = data[f.key];
        if (f.type === "checks") {
          if (v?.length) entries.push({ label: `${sec.title} — ${f.label === "Check all that apply" ? "" : f.label}`.replace(/ — $/, ""), value: v.join("; ") });
        } else if (v) {
          entries.push({ label: f.label, value: v });
        }
      })
    );
    return entries;
  };

  const submit = async () => {
    if (!signerName.trim()) { toast.error("Signer name is required"); return; }
    if (!sig) { toast.error("Please draw a signature"); return; }
    setSigning(true);
    try {
      if (!id) {
        const res = await api.post(`/document-library/${docKey}/sign`, {
          signer_name: signerName.trim(),
          relationship: relationship.trim(),
          signature_png: sig,
          entries: buildEntries(),
          fields: data,
        }, { timeout: 120000, responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${docKey}_signed.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Signed PDF downloaded");
        navigate("/documents");
        return;
      }
      await api.post(`/clients/${id}/paperwork/${docKey}/sign`, {
        signer_name: signerName.trim(),
        relationship: relationship.trim(),
        signature_png: sig,
        entries: buildEntries(),
      }, { timeout: 120000 });
      toast.success("Document signed and stored");
      navigate(`/clients/${id}/paperwork`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="fill-sign-page">
      <button onClick={() => navigate(id ? `/clients/${id}/paperwork` : "/documents")} className="mb-4 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="back-to-paperwork">
        <ArrowLeft size={14} /> {id ? "Back to paperwork" : "Back to document library"}
      </button>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">{entry.title}</h1>
        {client && (
        <span className="rounded-lg border border-stone-200 bg-accent px-2.5 py-1 text-xs font-semibold text-accent-foreground">{client.forms?.intake?.full_name || client.client_code}</span>
        )}
      </div>

      {entry.signed && (
        <div className="mb-6 flex items-center gap-3 rounded-lg border border-[#27AE60]/30 bg-[#27AE60]/10 p-4 text-sm text-[#1e8449]" data-testid="already-signed-banner">
          <CheckCircle size={20} weight="fill" />
          <span>Signed electronically by <strong>{entry.signed.signer_name}</strong> on {String(entry.signed.signed_at).slice(0, 10)}. Signing again will replace the signed copy.</span>
          {entry.file && (
            <a href={`${api.defaults.baseURL}/clients/${id}/paperwork/${docKey}/download`} target="_blank" rel="noreferrer"
              className="ml-auto inline-flex items-center gap-1 font-medium hover:underline" data-testid="view-signed-pdf">
              <FilePdf size={16} /> View signed PDF
            </a>
          )}
        </div>
      )}

      {!viewOnly && (
      <button onClick={() => setShowOriginal((s) => !s)} className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-stone-900" data-testid="toggle-original">
        {showOriginal ? <EyeSlash size={16} /> : <Eye size={16} />} {showOriginal ? "Hide original document" : "View original document"}
      </button>
      )}
      {(viewOnly || showOriginal) && (
        <div className="mb-6" data-testid="original-preview">
          {origUrl ? (
            <>
              <iframe src={origUrl} title="Original document"
                className="h-[500px] w-full rounded-lg border border-stone-200 bg-white" data-testid="original-iframe" />
              <a href={origUrl} target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid="original-open-tab">
                <FilePdf size={14} /> Open in a new tab
              </a>
            </>
          ) : origErr ? (
            <div className="rounded-lg border border-stone-200 bg-accent p-4 text-sm text-stone-600" data-testid="original-error">
              Couldn't load the preview. Please sign out and back in, then try again.
            </div>
          ) : (
            <div className="flex h-[500px] w-full items-center justify-center rounded-lg border border-stone-200 bg-white" data-testid="original-loading">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          )}
        </div>
      )}

      {viewOnly && (
        <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="view-only-notice">
          <p className="text-sm text-stone-600">This document is informational — there's nothing to fill in. Review it above, then download or print a copy.</p>
          <Button onClick={downloadBase} className="mt-4 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="view-only-download">Download PDF</Button>
        </div>
      )}

      {!viewOnly && def?.meds && (
        <div className="mb-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="meds-sign-form">
          <h3 className="font-heading text-lg font-semibold text-stone-900">Medication List</h3>
          <div className="mt-4 space-y-2.5">
            <div className="grid grid-cols-[28px_1fr_110px_110px] items-center gap-2 text-xs font-medium uppercase tracking-wide text-stone-400">
              <span>#</span><span>Medication</span><span>Dosage</span><span>Frequency</span>
            </div>
            {medRows.map((r, i) => (
              <div key={i} className="grid grid-cols-[28px_1fr_110px_110px] items-center gap-2">
                <span className="text-xs text-stone-400">{i + 1}</span>
                <Input value={r.name} onChange={(e) => setMedRow(i, "name", e.target.value)} className="h-8 border-stone-300 bg-white text-sm" data-testid={`sign-med-name-${i}`} />
                <Input value={r.dosage} onChange={(e) => setMedRow(i, "dosage", e.target.value)} className="h-8 border-stone-300 bg-white text-sm" data-testid={`sign-med-dosage-${i}`} />
                <Input value={r.frequency} onChange={(e) => setMedRow(i, "frequency", e.target.value)} className="h-8 border-stone-300 bg-white text-sm" data-testid={`sign-med-frequency-${i}`} />
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <DictateButton testid="sign-med-notes-dictate" onText={(t) => setMedNotes((n) => (n ? n + " " : "") + t)} />
          </div>
          <Textarea rows={3} placeholder="Additional medications or notes" value={medNotes} onChange={(e) => setMedNotes(e.target.value)}
            className="mt-1.5 border-stone-300 bg-white text-sm" data-testid="sign-med-notes" />
        </div>
      )}

      {!viewOnly && (def?.sections || []).map((sec) => (
        <div key={sec.title} className="mb-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid={`sign-section-${sec.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <h3 className="font-heading text-lg font-semibold text-stone-900">{sec.title}</h3>
          <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">{sec.fields.map(renderField)}</div>
        </div>
      ))}

      {!viewOnly && !def && (
        <div className="mb-6 rounded-lg border border-stone-200 bg-accent p-4 text-sm text-stone-600" data-testid="ack-only-notice">
          This is an acknowledgement document — no fields to fill. Review the original document above, then sign below.
        </div>
      )}

      {!id && !viewOnly && (
        <div className="mb-6 rounded-lg border border-dashed border-stone-300 bg-stone-50 p-6" data-testid="paper-upload-section">
          <h3 className="font-heading text-base font-semibold text-stone-900">Already have this form signed on paper?</h3>
          <p className="mt-1 text-xs text-stone-500">Upload the filled paper copy (photo or PDF) — we'll file it to the person's record instead.</p>
          {paperPerson ? (
            <div className="mt-3 flex items-center gap-2">
              <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary" data-testid="paper-person-chip">
                {paperPerson.name} · {paperPerson.type}
              </span>
              <button onClick={() => setPaperPerson(null)} className="text-xs text-stone-400 hover:text-stone-700" data-testid="paper-person-clear">change</button>
            </div>
          ) : (
            <div className="relative mt-3">
              <Input value={paperQ} onChange={(e) => setPaperQ(e.target.value)} placeholder="Type the client or employee name…"
                className="border-stone-300 bg-white" data-testid="paper-person-search" />
              {paperHits.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-stone-200 bg-white shadow-lg" data-testid="paper-person-results">
                  {paperHits.map((p) => (
                    <button key={`${p.type}-${p.id}`} onClick={() => { setPaperPerson(p); setPaperHits([]); }}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-stone-50"
                      data-testid={`paper-person-${p.type}-${p.id}`}>
                      <span>{p.name}</span><span className="text-xs text-stone-400">{p.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input type="file" accept="application/pdf,image/*" onChange={(e) => setPaperFile(e.target.files?.[0] || null)}
              className="text-sm text-stone-600" data-testid="paper-file-input" />
            <Button onClick={uploadPaper} disabled={paperBusy || !paperPerson || !paperFile} variant="outline"
              className="border-stone-300 text-stone-700" data-testid="paper-upload-submit">
              {paperBusy ? "Filing…" : "Upload & file it"}
            </Button>
          </div>
        </div>
      )}

      {!viewOnly && (
      <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="signature-section">
        <h3 className="font-heading text-lg font-semibold text-stone-900">Signature</h3>
        <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Signer full name *</Label>
            <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} className="border-stone-300 bg-white" data-testid="signer-name" placeholder="Client or representative" />
          </div>
          <div className="space-y-1.5">
            <Label>Relationship to client (if representative)</Label>
            <Input value={relationship} onChange={(e) => setRelationship(e.target.value)} className="border-stone-300 bg-white" data-testid="signer-relationship" placeholder="e.g. Daughter, POA" />
          </div>
        </div>
        <div className="mt-5">
          <SignaturePad onChange={setSig} />
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate(id ? `/clients/${id}/paperwork` : "/documents")} className="border-stone-300 text-stone-700 hover:bg-stone-100" data-testid="sign-cancel">Cancel</Button>
          <Button onClick={submit} disabled={signing} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="sign-submit">
            {signing ? "Signing & storing..." : "Sign Document"}
          </Button>
        </div>
      </div>
      )}
    </div>
  );
}
